<script>
export default {
	props: ['project'],
	computed: {
		badge() {
			// "Web Games" -> "WEB GAME"; other categories pass through.
			return (this.project.category || '')
				.replace(/^Web Games$/i, 'Web Game')
				.toUpperCase();
		},
		summary() {
			// `details` is the one-liner; fall back to the longer description.
			return this.project.details || this.project.description || '';
		},
		tags() {
			// Skip the boilerplate HTML/CSS; surface what's technically telling.
			return (this.project.techs || [])
				.filter((t) => !/^(html|css)$/i.test(t))
				.slice(0, 3);
		},
		// Apps show a fanned stack of real screenshots instead of a flat cover
		// (set `coverStyle: 'screens'` on the project). Games keep their box art.
		screens() {
			if (this.project.coverStyle !== 'screens' || !this.project.imgs) {
				return null;
			}
			return this.project.imgs.slice(0, 3);
		},
	},
};
</script>

<template>
	<router-link
		:to="'/projects/' + project.slug"
		class="project-card group relative flex flex-col rounded-xl overflow-hidden bg-surface border border-line hover:border-line-strong transition duration-300"
		aria-label="Single Project"
	>
		<!-- Preview -->
		<div class="relative aspect-[16/10] overflow-hidden bg-surface-2">
			<template v-if="screens">
				<div
					v-for="(src, i) in screens"
					:key="i"
					class="phone"
					:class="'phone-' + i"
				>
					<img
						:src="src"
						:alt="project.title"
						class="w-full h-full object-cover object-top"
					/>
				</div>
			</template>
			<img
				v-else
				:src="project.img"
				:alt="project.title"
				class="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
			/>
			<!-- Category badge; z-10 because .phone elements set explicit z-index and an unindexed sibling would paint beneath them regardless of DOM order. -->
			<span
				class="absolute top-3 left-3 z-10 font-mono-ui text-[10px] tracking-[0.14em] text-accent-soft bg-ground/70 backdrop-blur-sm border border-white/5 rounded px-2 py-1"
			>
				{{ badge }}
			</span>
		</div>

		<!-- Body -->
		<div class="flex flex-col flex-1 p-5 text-left">
			<h3
				class="font-general-medium text-base sm:text-lg text-ink leading-snug"
			>
				{{ project.title }}
			</h3>
			<p
				class="clamp-2 font-general-regular text-sm text-ink-muted mt-2 leading-relaxed"
			>
				{{ summary }}
			</p>

			<div class="mt-4 flex items-end justify-between gap-3">
				<ul class="flex flex-nowrap gap-1.5 min-w-0 overflow-hidden">
					<li
						v-for="tech in tags"
						:key="tech"
						class="font-mono-ui text-[11px] text-ink-muted border border-line rounded px-2 py-0.5 whitespace-nowrap shrink-0"
					>
						{{ tech }}
					</li>
				</ul>
				<span
					class="text-accent shrink-0 transition-transform duration-300 group-hover:translate-x-1.5"
					aria-hidden="true"
				>
					&rarr;
				</span>
			</div>
		</div>
	</router-link>
</template>

<style scoped>
.clamp-2 {
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}
.project-card:hover {
	box-shadow: 0 24px 60px -32px rgba(124, 115, 255, 0.45);
}

/* Fanned screenshot stack, used when the project has coverStyle: 'screens'.
   Width is kept low enough that, even at the tall 9:18.5 phone ratio, the
   card has vertical margin to spare — so lifting/scaling the center phone
   on hover doesn't push its top past the thumb's overflow-hidden edge. */
.phone {
	position: absolute;
	top: 50%;
	width: 25%;
	aspect-ratio: 9 / 18.5;
	border-radius: 12px;
	overflow: hidden;
	border: 1px solid rgba(255, 255, 255, 0.09);
	box-shadow: 0 20px 36px -16px rgba(0, 0, 0, 0.7);
	background-color: #0c0c10;
	transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1),
		filter 0.45s ease;
}
.phone-0 {
	left: 28%;
	transform: translate(-50%, -50%) rotate(-8deg) scale(0.82);
	z-index: 1;
	filter: brightness(0.72);
}
.phone-1 {
	left: 50%;
	transform: translate(-50%, -51%) scale(1);
	z-index: 3;
}
.phone-2 {
	left: 72%;
	transform: translate(-50%, -50%) rotate(8deg) scale(0.82);
	z-index: 2;
	filter: brightness(0.72);
}
.project-card:hover .phone-0 {
	transform: translate(-58%, -52%) rotate(-11deg) scale(0.85);
	filter: brightness(0.92);
}
.project-card:hover .phone-1 {
	transform: translate(-50%, -53%) scale(1.04);
}
.project-card:hover .phone-2 {
	transform: translate(-42%, -52%) rotate(11deg) scale(0.85);
	filter: brightness(0.92);
}
</style>
